import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from "@/components/ui/form";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
// Note: Using simpler types since database.types.ts export may be unavailable
import { Shield, Save } from "lucide-react";

interface Agent {
    id: string;
    title?: string | null;
    description?: string | null;
    is_template?: boolean | null;
    hidden_template_instructions?: string | null;
    industry_id?: string | null;
    persona_id?: string | null;
    [key: string]: any;
}

interface Industry {
    id: string;
    name: string;
    emoji?: string | null;
}

interface Persona {
    id: string;
    avatar_name: string | null;
    type: string | null;
}

// Define the update data type for better type safety
type AgentUpdateData = Partial<{
    is_template: boolean;
    hidden_template_instructions: string | null;
    industry_id: string | null;
    persona_id: string | null;
    description: string | null;
}>;

interface AgentAdminTabProps {
    agent: Agent;
    updateAgent: (data: Partial<Agent>) => Promise<boolean>;
}

// Form validation schema for admin fields
const adminFormSchema = z.object({
    is_template: z.boolean().default(false),
    hidden_template_instructions: z.string().optional(),
    industry_id: z.string().optional(),
    persona_id: z.string().optional(),
    description: z.string().optional(),
});

type AdminFormValues = z.infer<typeof adminFormSchema>;

const AgentAdminTab: React.FC<AgentAdminTabProps> = ({ agent, updateAgent }) => {
    const [industries, setIndustries] = useState<Industry[]>([]);
    const [personas, setPersonas] = useState<Persona[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const { toast } = useToast();

    const form = useForm<AdminFormValues>({
        resolver: zodResolver(adminFormSchema),
        defaultValues: {
            is_template: agent.is_template || false,
            hidden_template_instructions: agent.hidden_template_instructions || "",
            industry_id: agent.industry_id || "",
            persona_id: agent.persona_id || "",
            description: agent.description || "",
        },
    });

    // Fetch industries and personas on component mount
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                // Fetch industries
                const { data: industriesData, error: industriesError } = await supabase
                    .from("industries")
                    .select("*")
                    .order("name");

                if (industriesError) throw industriesError;

                // Fetch personas
                const { data: personasData, error: personasError } = await supabase
                    .from("personas")
                    .select("*")
                    .order("avatar_name");

                if (personasError) throw personasError;

                setIndustries(industriesData || []);
                setPersonas(personasData || []);
            } catch (error) {
                console.error("Error fetching data:", error);
                toast({
                    variant: "destructive",
                    title: "Error",
                    description: "Failed to load industries and personas",
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [toast]);

    // Update form when agent changes
    useEffect(() => {
        form.reset({
            is_template: agent.is_template || false,
            hidden_template_instructions: agent.hidden_template_instructions || "",
            industry_id: agent.industry_id || "",
            persona_id: agent.persona_id || "",
            description: agent.description || "",
        });
    }, [agent, form]);

    const onSubmit = async (values: AdminFormValues) => {
        setIsSaving(true);
        try {
            const updateData: AgentUpdateData = {
                is_template: values.is_template,
                hidden_template_instructions: values.hidden_template_instructions || null,
                industry_id: values.industry_id || null,
                persona_id: values.persona_id || null,
                description: values.description || null,
            };

            const success = await updateAgent(updateData);

            if (success) {
                toast({
                    title: "Success",
                    description: "Admin settings updated successfully",
                });
            } else {
                // Handle the case where updateAgent returns false (silent failure)
                console.error("updateAgent returned false - update failed silently");
                toast({
                    variant: "destructive",
                    title: "Update Failed",
                    description: "Failed to update admin settings. Please check your permissions and try again.",
                });
            }
        } catch (error) {
            console.error("Error updating admin settings:", error);
            toast({
                variant: "destructive",
                title: "Error",
                description: error instanceof Error ? error.message : "Failed to update admin settings",
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-8">
                <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                            <Shield className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                            <CardTitle>Admin Settings</CardTitle>
                            <CardDescription>
                                Super admin controls for this agent
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                            {/* Template Toggle */}
                            <FormField
                                control={form.control}
                                name="is_template"
                                render={({ field }) => (
                                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                        <div className="space-y-0.5">
                                            <FormLabel className="text-base">
                                                Mark as Template
                                            </FormLabel>
                                            <FormDescription>
                                                When enabled, this agent will appear in the template selection for all users
                                            </FormDescription>
                                        </div>
                                        <FormControl>
                                            <Switch
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />

                            {/* Description Field */}
                            <FormField
                                control={form.control}
                                name="description"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Description</FormLabel>
                                        <FormControl>
                                            <Textarea
                                                placeholder="Agent description visible to users"
                                                className="min-h-[100px]"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            This description will be visible to users when they view or select this agent
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Hidden Template Instructions */}
                            <FormField
                                control={form.control}
                                name="hidden_template_instructions"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Hidden Template Instructions</FormLabel>
                                        <FormControl>
                                            <Textarea
                                                placeholder="Internal instructions for this template (not visible to users)"
                                                className="min-h-[100px]"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            These instructions are only visible to super admins and are used for internal documentation
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Industry Selection */}
                            <FormField
                                control={form.control}
                                name="industry_id"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Industry</FormLabel>
                                        <Select
                                            onValueChange={(value) => field.onChange(value === "none" ? "" : value)}
                                            value={field.value || "none"}
                                        >
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select an industry" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value="none">No Industry</SelectItem>
                                                {industries.map((industry) => (
                                                    <SelectItem key={industry.id} value={industry.id}>
                                                        {industry.emoji} {industry.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormDescription>
                                            Associate this agent with a specific industry
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Persona Selection */}
                            <FormField
                                control={form.control}
                                name="persona_id"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Persona</FormLabel>
                                        <Select
                                            onValueChange={(value) => field.onChange(value === "none" ? "" : value)}
                                            value={field.value || "none"}
                                        >
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select a persona" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value="none">No Persona</SelectItem>
                                                {personas.map((persona) => (
                                                    <SelectItem key={persona.id} value={persona.id}>
                                                        {persona.avatar_name} ({persona.type})
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormDescription>
                                            Associate this agent with a specific persona type
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Save Button */}
                            <div className="flex justify-end">
                                <Button type="submit" disabled={isSaving}>
                                    {isSaving ? (
                                        <>
                                            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="h-4 w-4 mr-2" />
                                            Save Admin Settings
                                        </>
                                    )}
                                </Button>
                            </div>
                        </form>
                    </Form>
                </CardContent>
            </Card>
        </div>
    );
};

export default AgentAdminTab; 